import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../ui/Icon';
import { useToasts, ToastHost, PushToast } from '../ui/primitives';
import { initialsOf } from '../ui/couriers';
import CommandPalette, { Command } from './CommandPalette';
import MobileAdmin from './MobileAdmin';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { useDashboardStats, useAgentOnline } from './hooks';
import { printProductsReport } from './actions';
import { useQueryClient } from '@tanstack/react-query';
import { STOCK_KEYS, StockOverview, createStockRun, printStockSummary, stockErrorText, useInvalidateStock, useStockConfig } from '../magazyn/api';

const THEME_KEY = 'packflow.admin_theme';

const STR: Record<string, Record<string, string>> = {
  pl: { dashboard: 'Dashboard', orders: 'Zamówienia', ucs: 'Kolejka UCS', stats: 'Statystyki', editor: 'Edytor etykiet', settings: 'Ustawienia', superadmin: 'Super-admin', reports: 'Raporty', stock: 'Magazyn', module: 'Moduł', config: 'Konfiguracja', platform: 'Platforma' },
  en: { dashboard: 'Dashboard', orders: 'Orders', ucs: 'Packing queue', stats: 'Statistics', editor: 'Label editor', settings: 'Settings', superadmin: 'Super-admin', reports: 'Reports', stock: 'Stock', module: 'Module', config: 'Configuration', platform: 'Platform' },
};

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 760px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const on = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

export const ToastContext = React.createContext<PushToast>(() => undefined);
export const useAdminToast = () => React.useContext(ToastContext);

export default function AdminShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, claims, signOut } = useAuth();
  const { orgSlug, displayName } = useOrg();
  const connected = useAgentOnline();
  const [theme, setTheme] = useState<string>(() => localStorage.getItem(THEME_KEY) || 'light');
  const [lang, setLang] = useState<'pl' | 'en'>('pl');
  const [cmdk, setCmdk] = useState(false);
  const [toasts, push] = useToasts();
  const { data: stats } = useDashboardStats();
  const isMobile = useIsMobile();
  // Magazyn jest opcjonalny — pozycja w menu i akcje tylko, gdy moduł włączony.
  const { data: stockConfig } = useStockConfig();
  const stockOn = !!stockConfig?.enabled;
  const invalidateStock = useInvalidateStock();
  const qc = useQueryClient();
  const T = STR[lang];

  useEffect(() => { localStorage.setItem(THEME_KEY, theme); }, [theme]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdk((v) => !v); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const orgLabel = displayName || orgSlug || 'organizacja';
  const pendingBadge = stats?.pendingOrders ? String(stats.pendingOrders) : undefined;

  const nav = useMemo(() => {
    const sections: { grp: string; items: [string, string, string, string?, boolean?][] }[] = [
      { grp: T.module, items: [['/', T.dashboard, 'layout-dashboard'], ['/orders', T.orders, 'list', pendingBadge], ['/ucs', T.ucs, 'boxes'], ...(stockOn ? [['/magazyn', T.stock, 'warehouse'] as [string, string, string]] : []), ['/statistics', T.stats, 'bar-chart-3']] },
      { grp: T.config, items: [['/labels', T.editor, 'layout-template'], ['/settings', T.settings, 'settings']] },
      { grp: T.platform, items: [['/reports', T.reports, 'file-bar-chart']] },
    ];
    if (claims.is_super_admin) {
      sections[2].items.push(['/super-admin', T.superadmin, 'shield', undefined, true]);
    }
    return sections;
  }, [T, pendingBadge, claims.is_super_admin, stockOn]);

  const commands: Command[] = useMemo(() => [
    { grp: 'Nawigacja', id: '/', label: T.dashboard, icon: 'layout-dashboard', kbd: 'G D' },
    { grp: 'Nawigacja', id: '/orders', label: T.orders, icon: 'list', kbd: 'G O' },
    { grp: 'Nawigacja', id: '/statistics', label: T.stats, icon: 'bar-chart-3', kbd: 'G S' },
    { grp: 'Nawigacja', id: '/labels', label: T.editor, icon: 'layout-template', kbd: 'G E' },
    { grp: 'Nawigacja', id: '/settings', label: T.settings, icon: 'settings', kbd: 'G ,' },
    { grp: 'Nawigacja', id: '/reports', label: T.reports, icon: 'file-bar-chart' },
    { grp: 'Nawigacja', id: '/ucs', label: 'Kolejka UCS', icon: 'qr-code' },
    ...(stockOn ? [{ grp: 'Nawigacja', id: '/magazyn', label: T.stock, icon: 'warehouse' }] : []),
    ...(claims.is_super_admin ? [{ grp: 'Nawigacja', id: '/super-admin', label: T.superadmin, icon: 'shield' }] : []),
    { grp: 'Akcje', id: 'act-print', label: 'Drukuj raport produktów (na teraz)', icon: 'file-text', action: true },
    { grp: 'Akcje', id: 'act-report-since', label: 'Drukuj raport: nowe od ostatniego', icon: 'file-plus', action: true },
    ...(stockOn ? [
      { grp: 'Akcje', id: 'act-stock-summary', label: 'Magazyn: drukuj stan', icon: 'printer', action: true },
      { grp: 'Akcje', id: 'act-stock-run', label: 'Magazyn: lista do dobrania', icon: 'clipboard-list', action: true },
    ] : []),
    { grp: 'Akcje', id: 'act-theme', label: 'Przełącz motyw (jasny/ciemny)', icon: 'sun-moon', action: true },
  ], [T, claims.is_super_admin, stockOn]);

  async function handleAction(id: string) {
    if (id === 'act-theme') setTheme((t) => (t === 'light' ? 'dark' : 'light'));
    else if (id === 'act-print') {
      try { const r = await printProductsReport({ mode: 'now' }); push(r.message, r.ok ? 'ok' : 'info'); }
      catch (e: any) { push(e?.response?.data?.message || e?.response?.data?.error || 'Błąd drukowania raportu', 'err'); }
    } else if (id === 'act-stock-summary') {
      try { const r = await printStockSummary(); push(r.message || 'Stan magazynu wysłany do druku'); }
      catch (e) { push(stockErrorText(e, 'Nie udało się wydrukować stanu'), 'err'); }
    } else if (id === 'act-stock-run') {
      // Nowa lista anuluje nierozliczoną — dobrane wg niej pieczywo nie trafiłoby na stan.
      const open = qc.getQueryData<StockOverview>(STOCK_KEYS.overview)?.openRun;
      if (open && !window.confirm('Jest nierozliczona lista „Do dobrania”. Nowa lista ją zastąpi — to, co dobrano według starej, nie trafi na stan. Kontynuować?')) return;
      try {
        const r = await createStockRun(true);
        invalidateStock();
        push(r.message || (r.run ? 'Lista „Do dobrania” wysłana do druku' : 'Nic nie brakuje — lista niepotrzebna'), r.run && r.jobId ? 'ok' : 'info');
      } catch (e) { push(stockErrorText(e, 'Nie udało się utworzyć listy'), 'err'); }
    } else if (id === 'act-report-since') {
      try { const r = await printProductsReport({ mode: 'since' }); push(r.message, r.ok ? 'ok' : 'info'); }
      catch (e: any) { push(e?.response?.data?.message || e?.response?.data?.error || 'Błąd drukowania raportu', 'err'); }
    }
  }

  if (isMobile) {
    return (
      <ToastContext.Provider value={push}>
        <div data-theme={theme}>
          <ErrorBoundary resetKey={location.pathname}>
            <MobileAdmin theme={theme} setTheme={setTheme} orgLabel={orgLabel} push={push} connected={connected} />
          </ErrorBoundary>
          <ToastHost toasts={toasts} />
        </div>
      </ToastContext.Provider>
    );
  }

  return (
    <ToastContext.Provider value={push}>
      <div className="app" data-theme={theme}>
        {/* Insolitum Shell belt */}
        <div className="shell">
          <div className="uni"><span className="glyph"><Icon name="orbit" size={15} /></span> Insolitum <span className="muted">Universe</span></div>
          <button className="org"><Icon name="building-2" size={14} /> {orgLabel} <Icon name="chevron-down" size={14} /></button>
          <span className="muted" style={{ fontSize: 13 }}>/ Moduł: <b style={{ color: '#E8ECF3' }}>PackFlow</b></span>
          <div className="spacer" />
          <button className="kbd" onClick={() => setCmdk(true)}><Icon name="command" size={12} /> K</button>
          <button className="s-act" onClick={() => setLang((l) => (l === 'pl' ? 'en' : 'pl'))} title="Język">{lang.toUpperCase()}</button>
          <button className="s-act" onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))} title="Motyw"><Icon name={theme === 'light' ? 'moon' : 'sun'} size={17} /></button>
          <button className="s-act" title="Wyloguj" onClick={() => signOut()}><Icon name="log-out" size={17} /></button>
          <div className="avatar" title={user?.email || ''}>{initialsOf(user?.email?.split('@')[0]?.replace(/[._-]/g, ' ') || 'A')}</div>
        </div>

        <div className="body">
          {/* module sidebar */}
          <div className="sidebar">
            <div className="sb-brand">
              <div className="m"><Icon name="package-check" size={18} /></div>
              <div><div className="nm">PackFlow</div><div className="sub">Fulfillment</div></div>
            </div>
            <div className="sb-nav">
              {nav.map((sec) => (
                <div key={sec.grp}>
                  <div className="sb-sec">{sec.grp}</div>
                  {sec.items.map(([path, label, icon, badge, warn]) => (
                    <button
                      key={path}
                      className={`sb-item ${location.pathname === path ? 'active' : ''}`}
                      onClick={() => navigate(path)}
                    >
                      <Icon name={icon} size={18} /> {label}
                      {badge && <span className={`badge ${warn ? 'warn' : ''}`}>{badge}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <div className="sb-foot">
              <div className="station">
                <span className="dot" style={!connected ? { background: 'var(--warning)' } : undefined} />
                Agent on-prem · {connected ? 'połączony' : 'offline (kolejka)'}
              </div>
            </div>
          </div>

          {/* content */}
          <div className="content"><ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary></div>
        </div>

        {cmdk && <CommandPalette commands={commands} onClose={() => setCmdk(false)} onNav={(p) => navigate(p)} onAction={handleAction} />}
        <ToastHost toasts={toasts} />
      </div>
    </ToastContext.Provider>
  );
}
